import { Espacio, Reservation } from '../../../models/sala.model';
import { formatClockTime } from '../../../utils/format';

export type Tone = 'blue' | 'orange' | 'green' | 'violet' | 'gray';

const SPACE_IMAGES_BASE_URL = 'https://storage.googleapis.com/thehubp/spaces/';

/** Convierte el nombre relativo que entrega el backend en una URL utilizable. */
export function resolveDashboardImage(url: string | null | undefined, fallback: string): string {
  const value = url?.trim();
  if (!value) return fallback;
  if (/^(?:https?:|data:|blob:|\/)/i.test(value) || value.startsWith('dashboard-assets/')) return value;
  return `${SPACE_IMAGES_BASE_URL}${value.replace(/^\/+/, '')}`;
}

const MONEY = new Intl.NumberFormat('es-SV', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
});

export function formatMoney(value: string | number | null | undefined): string {
  const amount = Number(value);
  return Number.isFinite(amount) ? MONEY.format(amount) : '';
}

export function formatHourMin(minutes: number): string {
  const total = Math.max(0, Math.min(24 * 60, Math.round(minutes)));
  const h = Math.floor(total / 60) % 24;
  const m = total % 60;
  return formatClockTime(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
}

export function formatTimeRange(start: string | null, end: string | null): string {
  const short = (t: string | null) => formatClockTime(t);
  return end ? `${short(start)} – ${short(end)}` : short(start);
}

export function formatDayLabel(iso: string, withYear = true): string {
  const date = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(date.getTime())) return '';
  const parts = new Intl.DateTimeFormat('es-SV', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    ...(withYear ? { year: 'numeric' as const } : {}),
  }).format(date);
  return parts.charAt(0).toUpperCase() + parts.slice(1).replace(/\./g, '');
}

export function formatLongDay(iso: string): string {
  const date = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(date.getTime())) return '';
  const raw = new Intl.DateTimeFormat('es-SV', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(date);
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

export function greeting(hour = new Date().getHours()): string {
  if (hour < 12) return 'Buenos días,';
  if (hour < 19) return 'Buenas tardes,';
  return 'Buenas noches,';
}

/**
 * Color del bloque según el estado REAL de la reserva. Nunca es el único
 * indicador: el bloque muestra además título, horario y una etiqueta.
 */
export function reservationTone(reservation: Reservation): Tone {
  if (reservation.status === 'completed') return 'green';
  if (reservation.status === 'pending') return 'orange';
  if (reservation.payment_status === 'pending' || reservation.payment_status === 'failed') return 'violet';
  return 'blue';
}

export function reservationStatusLabel(reservation: Reservation): string {
  if (reservation.status === 'pending') return 'Por confirmar';
  if (reservation.status === 'completed') return 'Finalizada';
  if (reservation.status === 'cancelled') return 'Cancelada';
  return reservation.payment_status === 'paid' ? 'Confirmada y pagada' : 'Confirmada';
}

export function paymentLabel(reservation: Reservation): string {
  if (reservation.payment_status === 'paid') return 'Pagado';
  if (reservation.payment_status === 'failed') return 'Pago fallido';
  if (reservation.payment_status === 'refunded') return 'Reembolsado';
  return 'Pago pendiente';
}

export function customerName(reservation: Reservation): string {
  return (
    reservation.customer?.name ||
    reservation.customer_name ||
    reservation.guest_name ||
    'Cliente sin nombre'
  );
}

/** Devuelve exclusivamente una fotografia real registrada para el espacio. */
export function spaceImage(space: Espacio | null | undefined): string | null {
  const own = space?.images?.find(image => !!image?.image_url)?.image_url;
  return own ? resolveDashboardImage(own, '') : null;
}

export function peopleLabel(count: number): string {
  return count === 1 ? '1 persona' : `${count} personas`;
}

/**
 * Texto relativo real ("En 2 h", "En 25 min", "En 3 días") hasta el inicio de
 * una reserva. Devuelve cadena vacía si ya empezó o si la fecha no es válida.
 */
export function timeUntil(dateIso: string, startTime: string | null, now = new Date()): string {
  if (!dateIso) return '';
  const [h, m] = (startTime || '00:00').split(':').map(Number);
  const target = new Date(`${dateIso}T00:00:00`);
  if (Number.isNaN(target.getTime())) return '';
  target.setHours(Number.isFinite(h) ? h : 0, Number.isFinite(m) ? m : 0, 0, 0);
  const diff = Math.round((target.getTime() - now.getTime()) / 60000);
  if (diff <= 0) return '';
  if (diff < 60) return `En ${diff} min`;
  const hours = Math.round(diff / 60);
  if (hours < 24) return `En ${hours} h`;
  const days = Math.round(hours / 24);
  return days === 1 ? 'Mañana' : `En ${days} días`;
}
